import { useDocumentVisibility, useIntervalFn } from '@vueuse/core'
import { isRef, ref, watch } from 'vue'
import type { Ref } from 'vue'

import { useFetchStore } from '@/stores/fetch'

/**
 * A poll interval in milliseconds — a number, or a source read at the end of every cycle.
 *
 * The dynamic forms exist for one row of §9.2's own table: the command queue polls **3 s while
 * a command is non-terminal, else 20 s**. That is one subscription whose rate depends on what
 * the last answer contained, not two pollers — and a screen that solved it with two would have
 * to keep them from overlapping, which is the property `usePoll` exists to provide.
 */
export type PollInterval = number | Ref<number> | (() => number)

export interface PollOptions {
    /** Base interval in milliseconds (§9.2 table: 3 s install, 5 s overview, 15 s dashboard, …). */
    interval: PollInterval
    /** Run `fn` once immediately, then on every tick. Default `true`. */
    immediate?: boolean
    /** Jitter fraction applied to every cycle so N open tabs do not align. Default 0.1 (±10 %). */
    jitter?: number
}

/**
 * The interval as it stands *now*. It is read once per cycle rather than watched, because a
 * rate change is only ever acted on when the next delay is computed anyway — and reading it
 * there means a caller can derive it from data the tick itself just fetched.
 */
const readInterval = (interval: PollInterval): number => {
    if (typeof interval === 'number') return interval
    if (isRef(interval))              return interval.value

    return interval()
}

const withJitter = (base: number, jitter: number): number =>
    Math.max(250, Math.round(base * (1 + (Math.random() * 2 - 1) * jitter)))

/**
 * The house poller (§9.2). Visibility-gated (pauses when the tab is hidden, fires once when it
 * comes back), jittered ±10 % on every cycle, never overlapping itself, and auto-disposed with
 * the calling component's effect scope — which is why a poll is always started from a component
 * and never from a store, where it would leak.
 */
export const usePoll = (fn: () => Promise<void>, opts: PollOptions) => {
    const jitter     = opts.jitter ?? 0.1
    const delay      = ref(withJitter(readInterval(opts.interval), jitter))
    const visibility = useDocumentVisibility()

    let inFlight = false

    const tick = async () => {
        if (inFlight || visibility.value === 'hidden') return
        inFlight = true
        try {
            await fn()
        } finally {
            inFlight = false
            // Re-jitter, which restarts the timer — and re-read the rate, so a queue that just
            // went quiet drops to its slow interval on the tick that observed it.
            delay.value = withJitter(readInterval(opts.interval), jitter)
        }
    }

    const { pause, resume, isActive } = useIntervalFn(tick, delay)

    watch(visibility, (state) => {
        if (state === 'visible') {
            void tick()
            resume()
        } else {
            pause()
        }
    })

    if (opts.immediate !== false) void tick()

    return { pause, resume, isActive, trigger: tick }
}

// ---------------------------------------------------------------------------------------------
// Conditional GET — the client half of §9.2's ETag contract (W11.5)
// ---------------------------------------------------------------------------------------------

/** One answered conditional request. Exactly one of `data` and `unchanged` carries the news. */
export interface PolledResponse<T> {
    /** The HTTP status as it arrived: `200`, `304`, or whatever went wrong. */
    status: number
    /** The document, on a `200` only. A `304` deliberately carries no body, and a failure none either. */
    data: T | null
    /** `true` on a `304` — the caller's copy is current and **nothing should re-render**. */
    unchanged: boolean
    /** `200` or `304`. A `304` is a *successful* poll: "the document is unchanged as of now" is an answer. */
    ok: boolean
    /**
     * The house error envelope on a failure, for `parseError` to localize — `null` otherwise.
     *
     * It is carried rather than dropped because the envelope's integer `code` **is** the SPA's
     * localization key (§6, §9.5): a caller handed only a status can say "could not load" and
     * nothing more specific, which throws away the one field the error contract exists to deliver.
     */
    error: unknown
}

/**
 * A conditional GET that remembers its own validator — §9.2's *"ETag/304 … a hard backend
 * contract"*, on the client side, in one place (W11.5).
 *
 * # Why it is a factory and not a function
 *
 * The validator is per **subscription**, not per call: it is the tag of the last body this
 * particular screen accepted, and it is meaningless to any other screen. A module-level cache keyed
 * by URL would leak one component's freshness into another's — two tabs polling the same instance
 * would each believe the other's 304 confirmed their own render — so the state lives in a closure
 * one caller owns and disposes with.
 *
 * # The validator is bound to the exact path it came from
 *
 * A paged, filtered list (`/issues?page=2&severity=critical`) has one ETag per query, so the tag is
 * stored **with the path that produced it** and is only offered back for a byte-identical path. A
 * filter change is then an unconditional request that establishes a new validator, rather than a
 * conditional one that can only ever miss — and a 304 can never confirm a document the screen is
 * not showing.
 *
 * # A failed poll keeps the validator
 *
 * A 5xx or a dropped connection says nothing about whether the resource changed, and the copy on
 * screen is still the last one the server confirmed. Dropping the tag there would turn every
 * transient failure into a full re-transfer on the next tick, which is exactly the traffic the
 * contract exists to avoid. `reset()` is the explicit way to forget — used when the *subject*
 * changes (a different instance), where a stale tag could confirm the wrong document entirely.
 *
 * # Not every endpoint validates yet, and sending the header anyway is free
 *
 * `GET /instances/:id/state`, `GET /dashboard` and `GET /issues` answer with an `ETag` today
 * (`HTTP_API/InstanceState.go`, `HTTP_API/Dashboard.go`). The list endpoints behind the instance
 * list and the command queue do not, and a request that offers `If-None-Match` to a server holding
 * no validator is simply answered `200` — so every poll in the SPA is wired through this helper and
 * each one starts paying the moment its endpoint grows a validator, with no second edit here.
 */
export const useConditionalGet = <T>() => {
    /** The validator from the last accepted 200, and the exact path it belongs to. */
    let validator = ''
    let validated = ''

    const get = async (path: string): Promise<PolledResponse<T>> => {
        const headers: Record<string, string> = {}
        if (validator && validated === path) headers['If-None-Match'] = validator

        const { statusCode, data, response } = await useFetchStore()
            .fetch(path, { headers })
            .get()
            .json<T>()

        const status = statusCode.value ?? 0

        // 304 first: the status is the only thing that separates "nothing changed" from "nothing
        // answered" — a 304 carries no body, so `data` is empty on both.
        if (status === 304) return { status, data: null, unchanged: true, ok: true, error: null }

        if (status === 200 && data.value) {
            validator = response.value?.headers.get('ETag') ?? ''
            validated = validator ? path : ''

            return { status, data: data.value, unchanged: false, ok: true, error: null }
        }

        // On a failure `data` holds the parsed error envelope rather than the document
        // (`stores/fetch.ts`'s `updateDataOnError`), so it travels out as `error` for `parseError`.
        return { status, data: null, unchanged: false, ok: false, error: data.value }
    }

    /** Forget the validator — the subject changed, so a 304 would confirm the wrong document. */
    const reset = () => {
        validator = ''
        validated = ''
    }

    return { get, reset }
}

